import React, { useId, useState } from 'react';
import { CheckoutModal } from '../../components/CheckoutModal';
import { PhotoPicker } from '../forms/PhotoPicker';
import { StarRating } from '../forms/StarRating';
import { isThemePreview } from '../../api/previewVisit';
import { isBuilderPage } from './ElementPlaceholder';
import { BODY_MAX, EMPTY_DRAFT, NAME_MAX, PHONE_MAX, draftProblems, sendDraft, stillWrong, type DraftField, type DraftSending, type ReviewDraft } from './reviewDraft';
import type { ReviewWriteWords } from './reviewWriteWords';
import './elementReviewWriter.css';

// A product page's "Write a review", as Shopify's review apps put it under the reviews: a button that opens a short
// form — the stars, a name, the phone they ordered with, their words and photos — and a thanks that says the shop
// reads a review before it shows. What was typed stays through a failed send.

/** The most photos one review may carry, the server's own bound. */
const MAX_PHOTOS = 6;

/** What the form needs: its words, what it holds, what is wrong, and how sending stands. */
type FormProps = {
  words: ReviewWriteWords;
  draft: ReviewDraft;
  onDraft: (next: ReviewDraft) => void;
  problems: Partial<Record<DraftField, string>>;
  sending: DraftSending;
  onSend: () => void;
};

// Draws one field's label over its control, and under it the sentence that says what is missing.
const Field: React.FC<{ id: string; label: string; hint?: string; problem?: string; children: React.ReactNode }> = ({ id, label, hint, problem, children }) => (
  <div className="xe-review-field">
    <label htmlFor={id}>{label}</label>
    {children}
    {hint && <span className="xe-review-hint" id={`${id}-hint`}>{hint}</span>}
    {problem && <span className="xe-review-problem" id={`${id}-problem`} role="alert">{problem}</span>}
  </div>
);

// Draws the form: the stars first, then who is writing, what they say and their photos, then the send button.
const WriterForm: React.FC<FormProps> = ({ words, draft, onDraft, problems, sending, onSend }) => {
  const id = useId();
  // Changes the fields given and keeps the rest as typed.
  const set = (patch: Partial<ReviewDraft>) => onDraft({ ...draft, ...patch });
  const busy = sending === 'sending';
  const refused = sending === 'failed' || sending === 'tooMany' ? words[sending] : '';
  return (
    <form className="xe-review-form" noValidate onSubmit={(event) => { event.preventDefault(); onSend(); }}>
      <div className="xe-review-field">
        <span className="xe-review-label" aria-hidden>{words.rating}</span>
        <StarRating name={`${id}-stars`} value={draft.rating} max={5} label={words.rating} language={words.language} onChange={(rating) => set({ rating })}
          invalid={Boolean(problems.rating)} describedBy={problems.rating ? `${id}-rating-problem` : undefined} />
        {problems.rating && <span className="xe-review-problem" id={`${id}-rating-problem`} role="alert">{problems.rating}</span>}
      </div>
      <Field id={`${id}-name`} label={words.name} problem={problems.name}>
        <input id={`${id}-name`} autoComplete="name" maxLength={NAME_MAX} value={draft.name} aria-invalid={Boolean(problems.name) || undefined} onChange={(e) => set({ name: e.target.value })} />
      </Field>
      <Field id={`${id}-phone`} label={words.phone} hint={words.phoneHint}>
        <input id={`${id}-phone`} type="tel" dir="ltr" autoComplete="tel" maxLength={PHONE_MAX} value={draft.phone} aria-describedby={`${id}-phone-hint`} onChange={(e) => set({ phone: e.target.value })} />
      </Field>
      <Field id={`${id}-body`} label={words.review} hint={words.reviewHint} problem={problems.body}>
        <textarea id={`${id}-body`} rows={4} maxLength={BODY_MAX} value={draft.body} dir="auto" aria-invalid={Boolean(problems.body) || undefined} aria-describedby={`${id}-body-hint`} onChange={(e) => set({ body: e.target.value })} />
      </Field>
      <PhotoPicker id={`${id}-photos`} className="xe-review-photos" buttonLabel={words.photos} urls={draft.photos} limit={MAX_PHOTOS} onChange={(photos) => set({ photos })} />
      {refused && <p className="xe-review-problem" role="alert">{refused}</p>}
      <button type="submit" className="xe-review-send" disabled={busy} aria-busy={busy}>{busy ? words.sending : words.send}</button>
    </form>
  );
};

// Draws the thanks once a review is kept, with the line that says it shows after the shop has read it.
const Thanks: React.FC<{ words: ReviewWriteWords; onClose: () => void }> = ({ words, onClose }) => (
  <div className="xe-review-thanks" role="status">
    <p className="xe-review-thanks-title">{words.thanks}</p>
    <p>{words.thanksHint}</p>
    <button type="button" className="xe-review-send" onClick={onClose}>{words.close}</button>
  </div>
);

// Draws the button and, once pressed, the form over the page; the builder, a theme preview and a sample product only
// show the button, so nobody trying a theme sends a real review to the demo shop.
export const ReviewWriter: React.FC<{ productId: string; words: ReviewWriteWords }> = ({ productId, words }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<ReviewDraft>(EMPTY_DRAFT);
  const [problems, setProblems] = useState<Partial<Record<DraftField, string>>>({});
  const [sending, setSending] = useState<DraftSending>('idle');

  // Checks the draft, then sends it; a kept review clears the form, a refused one keeps every word typed.
  const send = async () => {
    const found = draftProblems(draft, words);
    setProblems(found);
    if (Object.keys(found).length > 0 || sending === 'sending') return;
    setSending('sending');
    const outcome = await sendDraft(productId, draft);
    setSending(outcome);
    if (outcome === 'sent') setDraft(EMPTY_DRAFT);
  };

  // Takes the shopper's edit and clears the problem of any field it put right.
  const edit = (next: ReviewDraft) => { setDraft(next); setProblems((shown) => stillWrong(shown, draftProblems(next, words))); };

  // Closes the dialog; after a kept review the next one starts from a fresh form.
  const close = () => { setOpen(false); if (sending === 'sent') setSending('idle'); };
  return (
    <>
      <button type="button" className="xe-review-write" onClick={() => { if (!isBuilderPage() && !isThemePreview() && productId) setOpen(true); }}>{words.write}</button>
      <CheckoutModal open={open} title={words.title} closeLabel={words.close} onClose={close}>
        <div className="xe-review-sheet">
          <h2 className="xe-review-title">{words.title}</h2>
          {sending === 'sent' ? <Thanks words={words} onClose={close} />
            : <WriterForm words={words} draft={draft} onDraft={edit} problems={problems} sending={sending} onSend={() => void send()} />}
        </div>
      </CheckoutModal>
    </>
  );
};
